import { useScrollControl } from '@hooks/useScrollControl'
import { useScrollBarStore } from '@stores/useScrollBarStore'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ScrollView as ReactScrollView,
  NativeScrollEvent,
  NativeSyntheticEvent,
  LayoutChangeEvent,
} from 'react-native'
import { isWeb } from 'tamagui'

interface IScrollProps {
  layoutMeasurement: { height: number }
  contentOffset: { y: number }
  contentSize: { height: number }
}

interface IUseScrollBarProps {
  maxHeight: number
  scrollBarId: string
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
}

interface IScrollBarState {
  displayScrollBar: boolean
  scrollPosition: number
  yPosition: number
}

const INITIAL_SCROLL_STATE: IScrollBarState = {
  displayScrollBar: false,
  scrollPosition: 0,
  yPosition: 0,
}

export function useScrollBar({
  maxHeight,
  scrollBarId,
  onScroll,
}: IUseScrollBarProps) {
  const [scrollState, setScrollState] =
    useState<IScrollBarState>(INITIAL_SCROLL_STATE)
  const [isInteracting, setIsInteracting] = useState(false)
  const { registerScrollBar, unregisterScrollBar } = useScrollBarStore()
  const { isScrollingContent, setIsScrollingContent } = useScrollControl()
  const scrollViewRef = useRef<ReactScrollView>(null)
  const trackRef = useRef<HTMLElement | null>(null)
  const contentSizeRef = useRef<number>(0)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isDraggingRef = useRef(false)

  const scrollTo = useCallback((y: number, animated = true) => {
    scrollViewRef.current?.scrollTo({
      y,
      animated: !isDraggingRef.current && animated,
    })
  }, [])

  useEffect(() => {
    registerScrollBar(scrollBarId, scrollTo)

    return () => {
      unregisterScrollBar(scrollBarId)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [scrollBarId, registerScrollBar, unregisterScrollBar, scrollTo])

  const updateScrollBarVisibility = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setScrollState((prev) => {
        const shouldDisplay = contentSizeRef.current > maxHeight
        if (prev.displayScrollBar === shouldDisplay) return prev
        return { ...prev, displayScrollBar: shouldDisplay }
      })
    }, 100)
  }, [maxHeight])

  const calculateScroll = useCallback(
    ({ layoutMeasurement, contentOffset, contentSize }: IScrollProps) => {
      if (layoutMeasurement.height === 0) return

      contentSizeRef.current = contentSize.height
      const value = (layoutMeasurement.height / contentSize.height) * 100
      const newYPosition =
        contentOffset.y / (contentSize.height / layoutMeasurement.height)

      setScrollState((prev) => {
        const shouldDisplay = value < 100 ? prev.displayScrollBar : false
        if (
          prev.scrollPosition === value &&
          prev.yPosition === newYPosition &&
          prev.displayScrollBar === shouldDisplay
        ) {
          return prev
        }
        return {
          displayScrollBar: shouldDisplay,
          scrollPosition: value,
          yPosition: newYPosition,
        }
      })

      if (value < 100) {
        updateScrollBarVisibility()
      }
    },
    [updateScrollBarVisibility],
  )

  const firstLoadScroll = useCallback(() => {
    scrollTo(1)
  }, [scrollTo])

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (maxHeight < event.nativeEvent.contentSize.height) {
        setScrollState((prev) =>
          prev.displayScrollBar ? prev : { ...prev, displayScrollBar: true },
        )
      }
      calculateScroll(event.nativeEvent)
      onScroll?.(event)
    },
    [maxHeight, calculateScroll, onScroll],
  )

  const handleScrollStart = useCallback(() => {
    if (isWeb) return
    setIsScrollingContent(true)
  }, [setIsScrollingContent])

  const handleScrollEnd = useCallback(() => {
    if (isWeb) return
    setIsScrollingContent(false)
  }, [setIsScrollingContent])

  const calculateScrollFromMouseY = useCallback(
    (mouseY: number, trackElement: HTMLElement) => {
      if (!trackElement || contentSizeRef.current === 0) return 0

      const trackRect = trackElement.getBoundingClientRect()
      const relativeY = mouseY - trackRect.top
      const trackHeight = trackRect.height
      const scrollableHeight = contentSizeRef.current - maxHeight

      const clampedY = Math.max(0, Math.min(relativeY, trackHeight))
      const scrollRatio = clampedY / trackHeight
      const scrollY = scrollRatio * scrollableHeight

      return Math.max(0, Math.min(scrollY, scrollableHeight))
    },
    [maxHeight],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!isWeb) return

      event.preventDefault()
      event.stopPropagation()

      const target = event.currentTarget as HTMLElement
      const mouseY = event.clientY

      isDraggingRef.current = true
      setIsInteracting(true)

      const scrollY = calculateScrollFromMouseY(mouseY, target)
      scrollTo(scrollY, false)
    },
    [calculateScrollFromMouseY, scrollTo],
  )

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isWeb || !trackRef.current) return

      event.preventDefault()

      const scrollY = calculateScrollFromMouseY(event.clientY, trackRef.current)
      scrollTo(scrollY, false)
    },
    [calculateScrollFromMouseY, scrollTo],
  )

  const handleMouseUp = useCallback(() => {
    if (!isWeb) return

    isDraggingRef.current = false
    setIsInteracting(false)
  }, [])

  useEffect(() => {
    if (!isWeb || !isDraggingRef.current) return

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isInteracting, handleMouseMove, handleMouseUp])

  useEffect(() => {
    const timerId = setTimeout(() => {
      updateScrollBarVisibility()
    }, 300)

    return () => clearTimeout(timerId)
  }, [updateScrollBarVisibility])

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const {
        nativeEvent: {
          layout: { height, y },
        },
      } = event
      firstLoadScroll()
      contentSizeRef.current = height
      updateScrollBarVisibility()
      calculateScroll({
        layoutMeasurement: { height: 0 },
        contentOffset: { y },
        contentSize: { height },
      })
    },
    [firstLoadScroll, updateScrollBarVisibility, calculateScroll],
  )

  const handleContentSizeChange = useCallback(
    (_: number, height: number) => {
      contentSizeRef.current = height
      updateScrollBarVisibility()
    },
    [updateScrollBarVisibility],
  )

  return {
    displayScrollBar: scrollState.displayScrollBar,
    scrollPosition: scrollState.scrollPosition,
    yPosition: scrollState.yPosition,
    isInteracting,
    scrollViewRef,
    trackRef,
    handleScroll,
    handleScrollStart,
    handleScrollEnd,
    handleMouseDown,
    handleLayout,
    handleContentSizeChange,
    isScrollingContent,
  }
}
